import * as React from 'react';

import _ from 'lodash';

import { Checkbox, StyleSheet, ToolbarTable, color, css } from '@coursera/coursera-ui';

import { CONTRACT_STATE_TYPES } from 'bundles/enterprise-admin-contracts/utils/AdminContractUtils';
import ModalFooter from 'bundles/enterprise-admin-users/components/ModalFooter';
import { CHECKED_BACKGROUND_COLOR, TABLE_HEIGHT } from 'bundles/enterprise-admin-users/constants';
import type { UserRoster } from 'bundles/enterprise-admin-users/types';
import { getFormattedContractType, renderUSDate } from 'bundles/enterprise-admin-users/utils';

import _t from 'i18n!nls/enterprise-admin-users';

const styles = StyleSheet.create({
  cell: {
    wordBreak: 'break-all',
  },
});

const { ACTIVE } = CONTRACT_STATE_TYPES;

type Props = {
  handleClose: () => void;
  setRoster: (roster: UserRoster) => void;
  onPreviousStep?: () => void;
  userRosters: UserRoster[];
  selectedRoster?: UserRoster;
  onContinue: () => void;
};

class ChooseContractTable extends React.Component<Props> {
  onCheckboxClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rosterId = e.target.value;
    this.onRosterChange(rosterId);
  };

  onRowClick = (event: React.MouseEvent, row: { key: string }) => {
    const { key } = row;
    this.onRosterChange(key);
  };

  onRosterChange = (rosterId: string) => {
    const { setRoster, userRosters } = this.props;
    const selectedRoster = userRosters.find((roster) => roster.id === rosterId);
    if (selectedRoster) {
      setRoster(selectedRoster);
    }
  };

  getRows = () => {
    const { userRosters, selectedRoster } = this.props;
    const selectedRosterId = selectedRoster && selectedRoster.id;
    const sortedRows = _.sortBy(userRosters, (roster) => roster?.contract?.name || roster?.contract?.id);

    return sortedRows.map((roster) => {
      const { contract } = roster;
      const utilization = contract?.utilization;
      const isNotActive = contract?.contractState !== ACTIVE;
      const isFull = utilization?.remaining === 0;
      const disabledText = isFull ? _t('Full') : _t('Inactive');
      const isDisabled = isFull || isNotActive;
      const isSelected = roster.id === selectedRosterId;
      return {
        key: roster.id,
        name: contract?.name ?? contract?.id,
        type: getFormattedContractType(contract?.limitType),
        remaining: utilization?.remaining,
        total: utilization?.total,
        pendingInvitations: roster.programInvitations?.paging.total,
        started: contract?.startsAt,
        ends: contract?.endsAt,
        radio: isDisabled ? (
          <span className="bold">{disabledText}</span>
        ) : (
          <Checkbox value={roster.id} onChange={this.onCheckboxClick} checked={isSelected} isRadio size={20} />
        ),
        isSelected,
        isDisabled,
      };
    });
  };

  getRowStyle = ({ isSelected, isDisabled }: { isSelected: boolean; isDisabled: boolean }) => {
    if (isSelected) return { backgroundColor: CHECKED_BACKGROUND_COLOR, cursor: 'pointer' };
    else if (isDisabled) return { backgroundColor: color.disabled, cursor: 'not-allowed' };
    return { cursor: 'pointer' };
  };

  disableContinue = () => {
    const { selectedRoster } = this.props;
    return !selectedRoster;
  };

  render() {
    const { handleClose, onPreviousStep, onContinue } = this.props;
    return (
      <div className="rc-ChooseContractTable">
        <h2 className="m-b-2">{_t('Choose contract')}</h2>
        <p className="m-b-2">
          {_t('Choose the contract from which invited members will consume memberships or enrollments')}
        </p>
        <ToolbarTable
          toolbar={[
            {
              type: 'search',
              placeholder: _t('Start typing the contract name to filter the list'),
              accessor: 'name',
            },
          ]}
          columns={[
            {
              accessor: 'radio',
              width: '10%',
            },
            {
              accessor: 'name',
              header: _t('Name'),
              sortable: true,
              width: '27.5%',
              cellRenderer: (children: React.ReactNode) => <span {...css(styles.cell)}>{children}</span>,
            },
            {
              accessor: 'type',
              header: _t('Contract Type'),
              width: '12.5%',
            },
            {
              accessor: 'remaining',
              header: _t('Remaining'),
              width: '10%',
            },
            {
              accessor: 'total',
              header: _t('Total Purchased'),
              width: '10%',
            },
            {
              accessor: 'pendingInvitations',
              header: _t('Pending Invitations'),
              width: '10%',
            },
            {
              accessor: 'started',
              header: _t('Started'),
              cellRenderer: renderUSDate,
              width: '10%',
            },
            {
              accessor: 'ends',
              header: _t('Ends'),
              cellRenderer: renderUSDate,
              width: '10%',
            },
          ]}
          rows={this.getRows()}
          rowStyle={this.getRowStyle}
          height={TABLE_HEIGHT}
          onClick={this.onRowClick}
        />
        <ModalFooter
          onContinue={onContinue}
          onContinueLabel={_t('Continue')}
          disabled={this.disableContinue()}
          onCancel={handleClose}
          onPrevious={onPreviousStep}
        />
      </div>
    );
  }
}

export default ChooseContractTable;
